import { api } from './api';
import { tokenStorage } from './authService';

// Sync types
export interface SyncNoteItem {
    id: string;
    localId?: string;
    date: string;
    title: string;
    content?: string;
    lunarDate?: string;
    eventType?: string;
    isRecurring: boolean;
    tags?: string[];
    reminder?: string;
    updatedAt: string;
    deleted: boolean;
}

export interface SyncNotesRequest {
    deviceId: string;
    lastSync?: string;
    notes: SyncNoteItem[];
}

export interface SyncNotesResponse {
    syncedAt: string;
    serverNotes: SyncNoteItem[];
    conflicts: SyncConflict[];
    created: number;
    updated: number;
    deleted: number;
}

export interface SyncConflict {
    clientNote: SyncNoteItem;
    serverNote: SyncNoteItem;
    resolution: 'server_wins' | 'client_wins';
}

export interface SyncStatus {
    lastSync?: string;
    pendingChanges: number;
    conflicts: number;
}

// Local storage keys
const LAST_SYNC_KEY = 'lunar_calendar_last_sync';
const PENDING_SYNC_KEY = 'lunar_calendar_pending_sync';

// Device ID helper
function getDeviceId(): string {
    const key = 'lunar_calendar_device_id';
    let deviceId = localStorage.getItem(key);
    if (!deviceId) {
        deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem(key, deviceId);
    }
    return deviceId;
}

export const syncStorage = {
    getLastSync: (): string | null => {
        return localStorage.getItem(LAST_SYNC_KEY);
    },
    setLastSync: (timestamp: string): void => {
        localStorage.setItem(LAST_SYNC_KEY, timestamp);
    },
    getPendingChanges: (): SyncNoteItem[] => {
        const data = localStorage.getItem(PENDING_SYNC_KEY);
        return data ? JSON.parse(data) : [];
    },
    addPendingChange: (note: SyncNoteItem): void => {
        const pending = syncStorage.getPendingChanges();
        // Replace if same ID exists
        const index = pending.findIndex(n => n.id === note.id);
        if (index >= 0) {
            pending[index] = note;
        } else {
            pending.push(note);
        }
        localStorage.setItem(PENDING_SYNC_KEY, JSON.stringify(pending));
    },
    clearPendingChanges: (): void => {
        localStorage.removeItem(PENDING_SYNC_KEY);
    }
};

export const syncService = {
    // Check if user is logged in
    isAuthenticated(): boolean {
        return !!tokenStorage.getToken();
    },

    // Sync notes with server
    async syncNotes(localNotes: SyncNoteItem[]): Promise<SyncNotesResponse> {
        const request: SyncNotesRequest = {
            deviceId: getDeviceId(),
            lastSync: syncStorage.getLastSync() || undefined,
            notes: localNotes
        };

        const response = await api.post<SyncNotesResponse>('/sync/notes', request);

        // Update last sync time
        syncStorage.setLastSync(response.data.syncedAt);
        syncStorage.clearPendingChanges();

        return response.data;
    },

    // Get sync status
    async getSyncStatus(): Promise<SyncStatus> {
        const response = await api.get<SyncStatus>('/sync/status');
        return response.data;
    },

    // Mark a note as pending sync (for offline changes)
    markPendingSync(note: SyncNoteItem): void {
        syncStorage.addPendingChange(note);
    },

    // Check if there are pending changes
    hasPendingChanges(): boolean {
        return syncStorage.getPendingChanges().length > 0;
    },

    // Get pending changes count
    getPendingCount(): number {
        return syncStorage.getPendingChanges().length;
    },

    // Auto sync if authenticated and has pending changes
    async autoSync(localNotes: SyncNoteItem[]): Promise<SyncNotesResponse | null> {
        if (!this.isAuthenticated()) {
            return null;
        }

        try {
            return await this.syncNotes(localNotes);
        } catch (error) {
            console.error('Auto sync failed:', error);
            return null;
        }
    }
};

export default syncService;
